<template>
  <div style="height:100%;">
    <commodity-analysis
      :Analytics="Analytics"
      :asset="Analytics.assetType"
      :colorTheme="colorTheme"
      :dataNest="dataNest"
      :idChart="idChart"
      :infoCharts="infoCharts"
      :infoLegends="infoLegends"
      :isPrices="isPrices"
      :loadingAll="loadingAll"
      :processData="processData"
      :renderChartSignal="renderChartSignal"
      :hasMinMaxRect="hasMinMaxRect"
      :customBarWidth="customBarWidth"
      :sizedLegends="sizedLegends"
      :isZoomY="true"
      @onTableFormat="onHoverChart"
    />
  </div>
</template>

<script>
import CommodityAnalysis from "../CommodityAnalysis.vue";
import { defaultAnalytics } from "@/_core/data/Analytics/AnalyticsHelpers.js";
import {
  colorSelection5,
  formatM,
  formatPercentSm
} from "@/_core/_helpers/_parametric_tools";
import { scaleOrdinal, interpolateRgbBasis, range, rgb as rgbD3 } from "d3";
import { replaceCharacters } from "../../../_core/_helpers/_helpers";

export default {
  components: { CommodityAnalysis },
  name: "CommodityStrategies",
  props: {
    Analytics: {
      required: true,
      default: defaultAnalytics()
    },
    colorTheme: { required: true, type: Boolean, default: true },
    loadingAll: { required: true, type: Boolean, default: false },
    processData: { required: true },
    renderChartSignal: { required: true, default: 0 },
    idChart: { required: true },
    isPrices: { default: false },
    hasMinMaxRect: { default: false }
  },
  data() {
    return {
      dataNest: "strategies",
      infoCharts: [],
      infoLegends: [],
      formatVars: {
        colorScale: null
      },
      sizedLegends: "2"
    };
  },
  created() {
    this.getInfoCharts();
  },
  computed: {
    isCurrency() {
      return this.Analytics.assetType === "currency";
    },
    customBarWidth() {
      return this.isPrices ? 0 : 0.65;
    }
  },
  methods: {
    getInfoCharts() {
      const allUniqueKeys = [];
      const dataUnitConfig = {};

      this.Analytics["rawData"][this.dataNest].forEach(d => {
        const keys = Object.keys(d);
        keys.forEach(key => {
          if (!allUniqueKeys.includes(key)) {
            const userSelectedAsset = this.Analytics.userSelections
              .selectedAsset[0];
            if (this.isCurrency) {
              const currName = userSelectedAsset.currency_name;
              dataUnitConfig["sUnit"] = currName.slice(0, 3);
              dataUnitConfig["unit"] = currName.slice(3);
            } else {
              dataUnitConfig["sUnit"] = userSelectedAsset.fk_idcurrency;
              dataUnitConfig["unit"] = userSelectedAsset.fk_idunit;
            }
            dataUnitConfig[`div${this.isPrices ? "P" : "Q"}`] = this.isPrices;

            if (key.startsWith("notional") && !this.isPrices) {
              dataUnitConfig[key] = d[key];
            }
            allUniqueKeys.push(key);
          }
        });
      });

      // Get different scale colors
      this.getColorScales(allUniqueKeys);
      const isPricesChart = this.isPrices ? "p_" : "q_";
      // Set info chart
      const chartInfo = this.setInfoChart(
        allUniqueKeys,
        isPricesChart,
        dataUnitConfig
      );

      if (this.isPrices) {
        chartInfo.push(this.getAveragePrices());
        chartInfo.push(this.getRefPrices());
      }

      Object.assign(this.infoCharts, chartInfo);

      if (!this.isPrices) {
        chartInfo.push(this.getTotalsQty());
      }
      Object.assign(this.Analytics, {
        [`table${this.isPrices ? "p_" : "q_"}${this.dataNest}`]: chartInfo
      });

      let changeInfoTable = this.Analytics[`tablep_${this.dataNest}`]?.concat(
        this.Analytics[`tableq_${this.dataNest}`]
      );

      if (!this.isPrices) {
        changeInfoTable = this.filterDataByKey(changeInfoTable, "label", "Min");
        changeInfoTable = this.filterDataByKey(changeInfoTable, "label", "Max");
      }

      this.$emit("changeInfoTable", changeInfoTable ? changeInfoTable : [{}]);
      this.infoLegends = this.createInfoLegends(this.infoCharts);
    },
    setInfoChart(allUniqueKeys, isPricesChart, dataUnitConfig) {
      const chartInfo = [];

      const data = allUniqueKeys
        .filter(
          d =>
            d.startsWith(isPricesChart) &&
            !d.endsWith("Perc") &&
            !d.includes("q_balance")
        )
        .map(d => {
          const dTransformed = replaceCharacters(d);

          const labelName = d
            .replace(isPricesChart, "")
            .split(" ")
            .map(this.capitalizeWord)
            .join(" ");

          // Get color scales
          const {
            color,
            colorWithOpacity,
            colorWithOpacityTable
          } = this.getColorScale(d);

          Object.assign(this.Analytics["userSelections"], {
            [`show${dTransformed}`]: true
          });

          return {
            label: labelName,
            labelTable: labelName,
            nameEvent: dTransformed.split(" ").join(""),
            elementName: dTransformed.split(" ").join(""),
            nameData: d,
            classNameIcon: this.isPrices && "fa fa-minus",
            colorBdr: color,
            colorBck: colorWithOpacity,
            colorBckTable: colorWithOpacityTable,
            unitPerc: `${d}Perc`,
            show: `show${dTransformed}`,
            formatF: formatM,
            formatPerc: formatPercentSm,
            isPrices: this.isPrices,
            figureElements: this.isPrices ? "bar" : "stackedBar",
            strokeWidth: this.isPrices ? 2 : 1,
            showText: !this.isPrices,
            keyMin: "q_min",
            keyMax: "q_max",
            typeFigure: this.isPrices
              ? { nameFigure: "gBar", toZero: false }
              : labelName === "Min" || labelName === "Max"
              ? { nameFigure: "", toZero: null }
              : { nameFigure: "multipleStackedBars", toZero: true },
            figureDetails: {
              barWidth: 0.66,
              offset: 0,
              height: 4,
              strokeWidth: 2
            },
            ...dataUnitConfig
          };
        });

      if (!this.isPrices) {
        chartInfo.push(this.getStrikeQty());
      }

      chartInfo.push(...data);
      return chartInfo;
    },
    getColorScales(allUniqueKeys) {
      const isPricesChart = this.isPrices ? "p_" : "q_";
      const chartInfo = allUniqueKeys.filter(
        d => d.startsWith(isPricesChart) && !d.endsWith("Perc")
      );

      const whiteColors = Array(3).fill("rgb(255, 255, 255)");
      let clrSel =
        chartInfo.indexOf("q_balance") > -1
          ? whiteColors.concat(colorSelection5)
          : colorSelection5;
      if (chartInfo.length > 1) {
        if (chartInfo.length <= clrSel.length) {
          this.formatVars.colorScale = scaleOrdinal()
            .domain(chartInfo)
            .range(range(chartInfo.length).map(t => clrSel[t]));
        } else {
          const colorInterpolator = interpolateRgbBasis(colorSelection5);
          this.formatVars.colorScale = scaleOrdinal()
            .domain(chartInfo)
            .range(
              range(chartInfo.length).map(t =>
                colorInterpolator(t / (chartInfo.length - 1))
              )
            );
        }
      } else {
        this.formatVars.colorScale = scaleOrdinal()
          .domain(chartInfo)
          .range([colorSelection5[0]]);
      }
    },
    getColorScale(d) {
      const color = this.formatVars.colorScale(d);
      const colorWithOpacity = rgbD3(color)
        .toString()
        .replace("rgb", "rgba")
        .replace(")", ", 0.8)");

      const colorWithOpacityTable = rgbD3(color)
        .toString()
        .replace("rgb", "rgba")
        .replace(")", ", 0.25)");

      return {
        color,
        colorWithOpacity,
        colorWithOpacityTable
      };
    },
    createInfoLegends(infoCharts) {
      infoCharts = this.filterDataByKey(infoCharts, "label", "Min");

      return infoCharts.map(chart => ({
        label: chart.label,
        show: chart.show,
        classNameIcon: chart.classNameIcon,
        colorBdr: chart.colorBdr,
        colorBck: chart.colorBck
      }));
    },
    filterDataByKey(data, key, value) {
      return data.filter(d => d[key] !== value);
    },
    onHoverChart(val) {
      this.$emit("onTableFormat", val);
    },
    capitalizeWord(word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    },
    getStrikeQty() {
      Object.assign(this.Analytics["userSelections"], {
        showq_balance: true
      });
      return {
        label: "Required",
        labelTable: "Required",
        colorBdr: "var(--expo-alpha)",
        colorBck: "var(--expo-alpha-3)",
        nameEvent: "q_balance",
        elementName: "q_balance",
        nameData: "q_balance",
        unitPerc: `-`,
        formatF: formatM,
        formatPerc: formatPercentSm,
        isPrices: false,
        typeFigure: { nameFigure: "gBar", toZero: true },
        figureDetails: { barWidth: 0.8, offset: 0, height: 4, strokeWidth: 2 },
        xOffsetLeft: 0,
        showText: false,
        show: "showq_balance",
        figureElements: "bar",
        colorBckTable: "var(--expo-alpha-3)"
      };
    },
    getAveragePrices() {
      Object.assign(this.Analytics["userSelections"], {
        showaverage: true
      });
      return {
        label: "Strat. Price",
        labelTable: "Strat. Price",
        nameEvent: "average",
        elementName: "average",
        nameData: "average",
        colorBckTable: "var(--graylightbackground-alpha4)",
        colorBdr: "var(--lighterbackground)",
        colorBck: "var(--graylightbackground-alpha4)",
        typeFigure: { nameFigure: "gBar", toZero: false },
        figureElements: "bar",
        strokeWidth: 2,
        show: "showaverage",
        classNameIcon: "fa fa-minus",
        showText: false,
        formatF: formatM,
        figureDetails: {
          barWidth: 0.66,
          offset: 0,
          height: 4,
          strokeWidth: 2
        },
        formatPerc: formatPercentSm,
        isPrices: true,
        totalRow: "table-totals"
      };
    },
    getRefPrices() {
      Object.assign(this.Analytics["userSelections"], {
        showrefPrice: true
      });
      return {
        label: "Ref. Price",
        labelTable: "Ref. Price",
        nameEvent: "refPrice",
        elementName: "refPrice",
        nameData: "refPrice",
        colorBdr: "var(--expo-alpha)",
        colorBck: "var(--expo-alpha-3)",
        colorBckTable: "var(--expo-alpha-3)",
        typeFigure: { nameFigure: "gBar", toZero: false },
        figureElements: "bar",
        strokeWidth: 2,
        show: "showrefPrice",
        classNameIcon: "fa fa-minus",
        formatF: formatM,
        formatPerc: formatPercentSm,
        figureDetails: {
          barWidth: 0.66,
          offset: 0,
          height: 4,
          strokeWidth: 2
        },
        isPrices: true,
        showText: false,
        totalRow: "table-totals"
      };
    },
    getTotalsQty() {
      return {
        label: "Totals",
        labelTable: "Totals",
        nameEvent: "totals",
        elementName: "totals",
        nameData: "totals",
        colorBckTable: "var(--graylightbackground-alpha4)",
        colorBdr: "var(--lighterbackground)",
        colorBck: "var(--graylightbackground-alpha4)",
        figureElements: "",
        formatF: formatM,
        formatPerc: formatPercentSm,
        isPrices: false,
        totalRow: "table-totals"
      };
    }
  }
};
</script>
